import React, { useEffect, useState, useCallback } from "react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  InstagramLogoIcon,
  ArrowsClockwiseIcon,
  ArrowSquareOutIcon,
  WarningCircleIcon,
  PaperPlaneTiltIcon,
  ChatCircleDotsIcon,
  TargetIcon,
  RewindIcon,
  PlugIcon,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { BackfillDialog } from "../components/BackfillDialog";

export default function Posts() {
  const [connections, setConnections] = useState([]);
  const [connId, setConnId] = useState("");
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingConns, setLoadingConns] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const cs = await api.listConnections();
        setConnections(cs);
        const enabled = cs.find((c) => c.enabled) || cs[0];
        if (enabled) setConnId(enabled.id);
      } finally {
        setLoadingConns(false);
      }
    })();
  }, []);

  const loadMedia = useCallback(async () => {
    if (!connId) {
      setMedia([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.listMedia(connId, 25);
      setMedia(res.media || []);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Failed to fetch posts. Check that the IG account is still connected.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }, [connId]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  if (loadingConns) {
    return (
      <Layout title="Posts" subtitle="Loading…">
        <div className="rounded-sm border border-zinc-200 bg-white p-12 text-center text-zinc-400">
          Loading connections…
        </div>
      </Layout>
    );
  }

  if (connections.length === 0) {
    return (
      <Layout title="Posts" subtitle="View posts from your connected Instagram accounts.">
        <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-zinc-300 bg-white py-24" data-testid="posts-no-connections">
          <PlugIcon size={64} weight="duotone" className="text-zinc-300" />
          <h3 className="mt-4 font-display text-2xl font-bold text-zinc-900">No connections yet</h3>
          <p className="mt-2 max-w-md text-center text-sm text-zinc-500">
            Connect a Facebook Page with an Instagram Business account to browse your posts and run backfills.
          </p>
          <Link to="/connections" data-testid="posts-go-connections"
            className="mt-6 inline-flex items-center gap-2 rounded-sm bg-amber-500 px-6 py-2.5 text-sm font-bold text-zinc-950 hover:bg-amber-600">
            <PlugIcon size={16} weight="bold" /> Go to Connections
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Posts"
      subtitle="Recent posts on the selected Instagram account, with per-post analytics and one-click backfill."
      action={
        <button onClick={loadMedia} data-testid="refresh-posts"
          className="inline-flex items-center gap-2 rounded-sm bg-zinc-950 px-6 py-2.5 text-sm font-bold text-white hover:bg-zinc-800">
          <ArrowsClockwiseIcon size={16} weight="bold" /> Refresh
        </button>
      }
    >
      <div className="mb-6 flex items-center gap-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Account</p>
        <select
          value={connId}
          onChange={(e) => setConnId(e.target.value)}
          data-testid="posts-connection-picker"
          className="rounded-sm border border-zinc-200 bg-white px-3 py-1.5 text-sm font-mono"
        >
          {connections.map((c) => (
            <option key={c.id} value={c.id}>@{c.ig_username || c.fb_page_name}</option>
          ))}
        </select>
      </div>

      {(() => {
        if (loading) {
          return <div className="rounded-sm border border-zinc-200 bg-white p-12 text-center text-zinc-400">Loading posts…</div>;
        }
        if (error) {
          return (
            <div className="flex items-start gap-3 rounded-sm border border-red-200 bg-red-50 p-6">
              <WarningCircleIcon size={24} weight="fill" className="mt-0.5 flex-shrink-0 text-red-600" />
              <div>
                <h3 className="font-display text-lg font-bold text-red-900">Couldn't load posts</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          );
        }
        if (media.length === 0) {
          return (
            <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-zinc-300 bg-white py-24">
              <InstagramLogoIcon size={64} weight="duotone" className="text-zinc-300" />
              <h3 className="mt-4 font-display text-2xl font-bold text-zinc-900">No posts found</h3>
              <p className="mt-2 max-w-md text-center text-sm text-zinc-500">
                This account has no posts yet, or the Page Access Token is missing the
                <code className="mx-1 rounded-sm bg-zinc-100 px-1 py-0.5 font-mono text-[10px]">instagram_basic</code>scope.
              </p>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {media.map((m) => <PostCard key={m.id} post={m} connId={connId} />)}
          </div>
        );
      })()}
    </Layout>
  );
}

const PostCard = ({ post, connId }) => {
  const [backfillOpen, setBackfillOpen] = useState(false);
  const thumb = post.thumbnail_url || post.media_url;
  const isVideo = post.media_type === "VIDEO";
  const caption = (post.caption || "").slice(0, 120);
  const timestamp = new Date(post.timestamp).toLocaleDateString();
  const stats = post.stats || { total_comments: 0, matched: 0, replies_sent: 0, dms_sent: 0, match_rate: 0 };
  const hasActivity = stats.total_comments > 0;

  const copyId = () => {
    navigator.clipboard.writeText(post.id);
    toast.success("Post ID copied");
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
         data-testid={`post-card-${post.id}`}>
      <div className="relative aspect-square overflow-hidden bg-zinc-100">
        {thumb ? (
          <img src={thumb} alt={caption} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full items-center justify-center text-zinc-400">
            <InstagramLogoIcon size={40} weight="duotone" />
          </div>
        )}
        {isVideo && (
          <span className="absolute right-2 top-2 rounded-sm bg-zinc-950/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            Video
          </span>
        )}
        {hasActivity && (
          <span className="absolute left-2 top-2 rounded-sm bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-950">
            {stats.match_rate}% match
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <p className="line-clamp-3 text-sm text-zinc-900">
          {caption || <span className="italic text-zinc-400">No caption</span>}
        </p>
        <div className="grid grid-cols-3 gap-1 rounded-sm border border-zinc-200 bg-zinc-50 p-2">
          <Stat icon={TargetIcon} value={stats.matched} total={stats.total_comments} label="matched" color="text-amber-600" testid={`stat-matched-${post.id}`} />
          <Stat icon={PaperPlaneTiltIcon} value={stats.replies_sent} label="replies" color="text-emerald-600" testid={`stat-replies-${post.id}`} />
          <Stat icon={ChatCircleDotsIcon} value={stats.dms_sent} label="dms" color="text-blue-600" testid={`stat-dms-${post.id}`} />
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <span className="font-mono text-[10px] text-zinc-500">{timestamp}</span>
          <div className="flex gap-1">
            <button onClick={() => setBackfillOpen(true)} data-testid={`backfill-btn-${post.id}`}
              className="inline-flex items-center gap-1 rounded-sm border border-amber-300 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-800 hover:bg-amber-100"
              title="Process old comments on this post">
              <RewindIcon size={11} weight="bold" /> Backfill
            </button>
            <button onClick={copyId}
              className="rounded-sm px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600 hover:bg-zinc-100"
              title={`Post ID: ${post.id}`}>
              Copy ID
            </button>
            {post.permalink && (
              <a href={post.permalink} target="_blank" rel="noreferrer"
                className="rounded-sm p-1 text-zinc-500 hover:bg-zinc-100 hover:text-amber-600">
                <ArrowSquareOutIcon size={14} weight="bold" />
              </a>
            )}
          </div>
        </div>
      </div>
      <BackfillDialog open={backfillOpen} onOpenChange={setBackfillOpen} post={post} connId={connId} />
    </div>
  );
};

const Stat = ({ icon: Icon, value, total, label, color, testid }) => (
  <div className="flex flex-col items-center justify-center gap-0.5 text-center" data-testid={testid}>
    <Icon size={14} weight="duotone" className={color} />
    <span className="font-display text-lg font-black leading-none tabular-nums text-zinc-950">
      {value}
      {total !== undefined && <span className="text-xs font-normal text-zinc-400">/{total}</span>}
    </span>
    <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">{label}</span>
  </div>
);
